/**
 * Procedural sound.
 *
 * Every effect is built from three ingredients: an oscillator, a buffer of
 * white noise and an envelope. No sample is ever downloaded, so there is no
 * asset pipeline, no licence to worry about and no loading screen.
 *
 * The scheduling functions are side-effect only: they hang nodes on the
 * AudioContext timeline and return immediately. Nothing here reads game state,
 * and nothing here can block a frame.
 */

import { MIX } from '../config/audio';
import type { OneShotSound } from './AudioManager';
import {
  bump,
  checkpoint,
  doorClose,
  doorOpen,
  doubleJump,
  finish,
  footstep,
  glassBreak,
  hanaAuki,
  jump,
  klapiNosto,
  klapiPesaan,
  land,
  makkaraLautaselle,
  oviLukossa,
  powerOff,
  stumble,
  tuoppiPoydalta,
} from './oneshots';
import {
  beerSip,
  fart,
  hannuEmerge,
  hannuSwipe,
  kempfMumble,
  kimmoShout,
  oksennus,
  shout,
} from './voices';

/** WebAudio gain ramps are exponential, and exponentials cannot reach zero. */
export const SILENT = 0.0001;
/** One full turn, for the hand-written oscillators. */
export const TAU = Math.PI * 2;

/** Everything a recipe needs: where to schedule, what to play into, how to vary. */
export interface Voicing {
  ctx: AudioContext;
  out: AudioNode;
  noise: AudioBuffer;
  /** Seeded, never Math.random: two identical runs sound identical. */
  rng: () => number;
}

interface Envelope {
  at: number;
  duration: number;
  peak: number;
  /** Time to full volume. Short = a click, long = a swell. */
  attack?: number;
}

interface ToneOptions extends Envelope {
  type: OscillatorType;
  freq: number;
  /** Glides to this frequency across `duration`. */
  freqEnd?: number;
  /** Wobble, in Hz of deviation at `hz` per second. Makes things organic. */
  vibrato?: { depth: number; hz: number };
  /** Shapes the raw waveform; a sawtooth without one is unlistenable. */
  filter?: { type: BiquadFilterType; freq: number; q?: number };
}

interface NoiseOptions extends Envelope {
  filter?: BiquadFilterType;
  freq: number;
  /** Sweeps the filter across `duration`. This is what makes a whoosh. */
  freqEnd?: number;
  q?: number;
}

function envelope(ctx: AudioContext, o: Envelope): GainNode {
  const gain = ctx.createGain();
  const attack = Math.min(o.attack ?? 0.006, o.duration * 0.5);
  gain.gain.setValueAtTime(SILENT, o.at);
  gain.gain.exponentialRampToValueAtTime(Math.max(SILENT * 2, o.peak), o.at + attack);
  gain.gain.exponentialRampToValueAtTime(SILENT, o.at + o.duration);
  return gain;
}

/** One pitched voice: oscillator -> optional filter -> envelope -> out. */
export function tone(v: Voicing, o: ToneOptions): void {
  const { ctx } = v;
  const osc = ctx.createOscillator();
  osc.type = o.type;
  osc.frequency.setValueAtTime(o.freq, o.at);
  if (o.freqEnd !== undefined && o.freqEnd !== o.freq) {
    osc.frequency.exponentialRampToValueAtTime(Math.max(1, o.freqEnd), o.at + o.duration);
  }

  if (o.vibrato) {
    const lfo = ctx.createOscillator();
    const depth = ctx.createGain();
    lfo.frequency.value = o.vibrato.hz;
    depth.gain.value = o.vibrato.depth;
    lfo.connect(depth).connect(osc.frequency);
    lfo.start(o.at);
    lfo.stop(o.at + o.duration + 0.05);
  }

  const gain = envelope(ctx, o);
  if (o.filter) {
    const biquad = ctx.createBiquadFilter();
    biquad.type = o.filter.type;
    biquad.frequency.value = o.filter.freq;
    biquad.Q.value = o.filter.q ?? 1;
    osc.connect(biquad).connect(gain).connect(v.out);
  } else {
    osc.connect(gain).connect(v.out);
  }

  osc.start(o.at);
  osc.stop(o.at + o.duration + 0.05);
}

/** One unpitched voice: noise -> swept filter -> envelope -> out. */
export function noiseBurst(v: Voicing, o: NoiseOptions): void {
  const { ctx } = v;
  const source = ctx.createBufferSource();
  source.buffer = v.noise;
  // Looping lets a short buffer feed a long burst, and the random start offset
  // keeps repeated footsteps from being bit-identical.
  source.loop = true;

  const biquad = ctx.createBiquadFilter();
  biquad.type = o.filter ?? 'lowpass';
  biquad.frequency.setValueAtTime(o.freq, o.at);
  if (o.freqEnd !== undefined && o.freqEnd !== o.freq) {
    biquad.frequency.exponentialRampToValueAtTime(Math.max(20, o.freqEnd), o.at + o.duration);
  }
  biquad.Q.value = o.q ?? 0.8;

  const gain = envelope(ctx, o);
  source.connect(biquad).connect(gain).connect(v.out);
  source.start(o.at, v.rng() * v.noise.duration);
  source.stop(o.at + o.duration + 0.05);
}

/** Schedules `sound` to start now. `volume` is the call site's own trim. */
export function playOneShot(v: Voicing, sound: OneShotSound, volume: number): void {
  const t = v.ctx.currentTime;
  const g = MIX.gain[sound] * volume;
  switch (sound) {
    case 'footstep':
      footstep(v, t, g);
      break;
    case 'jump':
      jump(v, t, g);
      break;
    case 'double-jump':
      doubleJump(v, t, g);
      break;
    case 'land':
      land(v, t, g);
      break;
    case 'door-open':
      doorOpen(v, t, g);
      break;
    case 'door-close':
      doorClose(v, t, g);
      break;
    case 'bump':
      bump(v, t, g);
      break;
    case 'stumble':
      stumble(v, t, g);
      break;
    case 'beer-sip':
      beerSip(v, t, g);
      break;
    case 'checkpoint':
      checkpoint(v, t, g);
      break;
    case 'finish':
      finish(v, t, g);
      break;
    case 'shout-finaali':
      shout(v, t, g, true);
      break;
    case 'hannu-emerge':
      hannuEmerge(v, t, g);
      break;
    case 'hannu-shout':
      shout(v, t, g, false);
      break;
    // The same two-syllable hail as Hannu's, because that is what it is: a man
    // across a yard getting your attention. Neither of them is `distant` - the
    // one that is comes from another building.
    case 'shout-kokki':
      shout(v, t, g, false);
      break;
    // And the man in the doorway, who is doing the same thing from closer.
    case 'shout-maysa':
      shout(v, t, g, false);
      break;
    // And seven of them at the far end of a hall, which is the same hail again
    // from the far side of a room.
    case 'shout-porukka':
      shout(v, t, g, false);
      break;
    // And the yard in front of the teltta, which is the same hail once more
    // and the only one of them that is angry. Still not `distant`: it comes
    // from behind the runner rather than from indoors.
    case 'shout-teltta':
      shout(v, t, g, false);
      break;
    // And the man at the sauna door, who is the one person in this list not
    // raising his voice at all. See `kempfMumble`.
    case 'shout-kempf':
      kempfMumble(v, t, g);
      break;
    case 'ovi-lukossa':
      oviLukossa(v, t, g);
      break;
    case 'tuoppi-poydalta':
      tuoppiPoydalta(v, t, g);
      break;
    case 'hana-auki':
      hanaAuki(v, t, g);
      break;
    case 'makkara-lautaselle':
      makkaraLautaselle(v, t, g);
      break;
    case 'oksennus':
      oksennus(v, t, g);
      break;
    case 'hannu-swipe':
      hannuSwipe(v, t, g);
      break;
    case 'klapi-nosto':
      klapiNosto(v, t, g);
      break;
    case 'klapi-pesaan':
      klapiPesaan(v, t, g);
      break;
    case 'power-off':
      powerOff(v, t, g);
      break;
    case 'glass-break':
    case 'bottle-break':
      glassBreak(v, t, g);
      break;
    case 'kimmo-shout':
      kimmoShout(v, t, g);
      break;
    case 'fart':
      fart(v, t, g);
      break;
  }
}
